import type { SortingState } from '@tanstack/react-table';

const STORAGE_PREFIX = 'list-filters:';

/** Save the current search params for a given path (e.g. "/multi-rater"). */
export function saveListFilters(basePath: string, searchParams: string): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(`${STORAGE_PREFIX}${basePath}`, searchParams);
  } catch {
    // sessionStorage full or unavailable — ignore
  }
}

/** Get the saved search params string for a given path, or empty string. */
export function getListFilters(basePath: string): string {
  if (typeof window === 'undefined') return '';
  try {
    return sessionStorage.getItem(`${STORAGE_PREFIX}${basePath}`) || '';
  } catch {
    return '';
  }
}

/** Remove every persisted table-state key. Call on logout or auth-refresh failure. */
export function clearAllListFilters(): void {
  if (typeof window === 'undefined') return;
  try {
    const keys: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key?.startsWith(STORAGE_PREFIX)) keys.push(key);
    }
    keys.forEach((k) => sessionStorage.removeItem(k));
  } catch {
    // ignore
  }
}

export interface PersistedDataTableState {
  sorting?: SortingState;
  pageIndex?: number;
  pageSize?: number;
  search?: string;
}

/** Persist client-side DataTable state under `list-filters:{persistKey}`. */
export function saveDataTableState(
  persistKey: string,
  state: PersistedDataTableState,
): void {
  if (typeof window === 'undefined') return;
  try {
    sessionStorage.setItem(
      `${STORAGE_PREFIX}${persistKey}`,
      JSON.stringify(state),
    );
  } catch {
    // sessionStorage full, JSON encode failure, or unavailable — ignore
  }
}

/** Read previously-persisted client-side DataTable state, or null. */
export function loadDataTableState(
  persistKey: string,
): PersistedDataTableState | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(`${STORAGE_PREFIX}${persistKey}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    return parsed as PersistedDataTableState;
  } catch {
    return null;
  }
}
